import { Plus, Search, SlidersHorizontal } from "lucide-react";
import type { Status } from "@/components/api/companiesApi";
import { STATUS_ORDER, type SortKey } from "@/lib/cms";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

interface CmsControlBarProps {
    search: string;
    onSearchChange: (value: string) => void;
    statuses: Status[];
    onStatusesChange: (value: Status[]) => void;
    overdueOnly: boolean;
    onOverdueOnlyChange: (value: boolean) => void;
    needsAction: boolean;
    onNeedsActionChange: (value: boolean) => void;
    sort: SortKey;
    onSortChange: (value: SortKey) => void;
    onNewCompany: () => void;
}

const SORT_LABELS: Record<SortKey, string> = {
    nextAction: "Next action due",
    lastContacted: "Last contacted (oldest)",
    offices: "Offices (most first)",
    name: "Name (A–Z)",
};

export function CmsControlBar({
    search,
    onSearchChange,
    statuses,
    onStatusesChange,
    overdueOnly,
    onOverdueOnlyChange,
    needsAction,
    onNeedsActionChange,
    sort,
    onSortChange,
    onNewCompany,
}: CmsControlBarProps) {
    const toggleStatus = (status: Status, checked: boolean) => {
        if (checked) {
            onStatusesChange([...statuses, status]);
        } else {
            onStatusesChange(statuses.filter((s) => s !== status));
        }
    };

    const searchActive = search.trim().length > 0;

    return (
        <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input
                    value={search}
                    onChange={(e) => onSearchChange(e.target.value)}
                    placeholder="Search companies, contacts, phone or email…"
                    className="pl-9"
                    aria-label="Search companies"
                />
            </div>

            {/* Status multi-select */}
            <Popover>
                <PopoverTrigger asChild>
                    <Button variant="outline" className="gap-2">
                        <SlidersHorizontal className="h-4 w-4" />
                        Status
                        <span className="text-xs text-muted-foreground">
                            ({searchActive ? "all" : statuses.length})
                        </span>
                    </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-60">
                    <div className="space-y-3">
                        <p className="text-sm font-medium">Show statuses</p>
                        {searchActive && (
                            <p className="text-xs text-muted-foreground">
                                A search is active — all statuses are searched.
                            </p>
                        )}
                        <div className="space-y-2">
                            {STATUS_ORDER.map((status) => (
                                <div key={status} className="flex items-center gap-2">
                                    <Checkbox
                                        id={`status-${status}`}
                                        checked={statuses.includes(status)}
                                        onCheckedChange={(checked) =>
                                            toggleStatus(status, checked === true)
                                        }
                                        disabled={searchActive}
                                    />
                                    <Label
                                        htmlFor={`status-${status}`}
                                        className={cn(
                                            "cursor-pointer font-normal",
                                            searchActive && "opacity-50",
                                        )}
                                    >
                                        {status}
                                    </Label>
                                </div>
                            ))}
                        </div>
                    </div>
                </PopoverContent>
            </Popover>

            <Button
                variant={overdueOnly ? "default" : "outline"}
                onClick={() => onOverdueOnlyChange(!overdueOnly)}
                aria-pressed={overdueOnly}
            >
                Overdue only
            </Button>
            <Button
                variant={needsAction ? "default" : "outline"}
                onClick={() => onNeedsActionChange(!needsAction)}
                aria-pressed={needsAction}
            >
                Needs action
            </Button>

            <Select value={sort} onValueChange={(v) => onSortChange(v as SortKey)}>
                <SelectTrigger className="w-[200px]" aria-label="Sort by">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    {(Object.keys(SORT_LABELS) as SortKey[]).map((key) => (
                        <SelectItem key={key} value={key}>
                            {SORT_LABELS[key]}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>

            <Button onClick={onNewCompany} className="gap-2 ml-auto">
                <Plus className="h-4 w-4" />
                New Company
            </Button>
        </div>
    );
}
